/**
 * CryptoPage (/crypto)
 *
 * Dedicated page for auto-detected external holdings (crypto/DeFi capital).
 * NinjaCash detects moves from transaction descriptions via the Watchlist
 * capital-move groups - nothing needs manual marking. The tracked value is
 * derived (moved out - moved back) plus user adjustments (market gains/
 * losses, one-tap "Set current value" sync).
 *
 * Everything here is presentation + local adjustments - transactions,
 * balances, analytics and sync are never modified (CRYPTO_HOLDINGS_PLAN.md).
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useHoldings } from '@hooks/useHoldings';
import { usePrivacyMode } from '@hooks/usePrivacyMode';
import { Modal, Button, Input, PrivacyToggle } from '@/components/shared';
import {
  HoldingsSnapshot,
  HoldingsAdjustment,
  addAdjustment,
  removeAdjustment,
  syncToValue,
} from '@services/holdings';
import { formatCurrency as formatCurrencyIntl, formatDate as formatDateIntl } from '@/utils/format';
import { useLanguage } from '@/i18n';
import './CryptoPage.css';

function fmt(amount: number): string {
  return formatCurrencyIntl(amount, 'USD', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

function fmtDate(date: Date | string): string {
  return formatDateIntl(new Date(date), { month: 'short', day: 'numeric', year: 'numeric' });
}

// ============================================================
// Adjust modal (two modes: value sync / gain-loss)
// ============================================================

type AdjustMode = 'sync' | 'gainloss';

interface AdjustModalProps {
  snapshot: HoldingsSnapshot | null;
  mode: AdjustMode;
  onClose: () => void;
  onSaved: () => void;
}

function AdjustModal({ snapshot, mode, onClose, onSaved }: AdjustModalProps) {
  const { t } = useLanguage();
  const [targetValue, setTargetValue] = useState('');
  const [glKind, setGlKind] = useState<'gain' | 'loss'>('loss');
  const [glAmount, setGlAmount] = useState('');
  const [note, setNote] = useState('');

  if (!snapshot) return null;

  const tracked = snapshot.trackedValue;
  const target = Number(targetValue);
  const hasTarget = targetValue.trim() !== '' && Number.isFinite(target);
  const delta = hasTarget ? Math.round((target - tracked) * 100) / 100 : 0;

  const glValue = Number(glAmount);
  const glValid = glAmount.trim() !== '' && Number.isFinite(glValue) && glValue > 0;

  const handleSave = async () => {
    if (mode === 'sync') {
      if (!hasTarget) return;
      await syncToValue(snapshot.group.id, tracked, target, note || undefined);
    } else {
      if (!glValid) return;
      await addAdjustment({
        groupId: snapshot.group.id,
        amount: glKind === 'gain' ? glValue : -glValue,
        kind: glKind,
        note: note || undefined,
      });
    }
    onSaved();
    onClose();
  };

  return (
    <Modal
      isOpen={!!snapshot}
      onClose={onClose}
      title={mode === 'sync' ? t('crypto.sync.title') : t('crypto.adjust.title')}
    >
      <div className="crypto-adjust-modal">
        {mode === 'sync' ? (
          <>
            <p className="crypto-modal-hint">{t('crypto.sync.hint')}</p>
            <div className="crypto-sync-current">
              <span>{t('crypto.sync.current')}</span>
              <strong>{fmt(tracked)}</strong>
            </div>
            <div className="form-group">
              <label>{t('crypto.sync.target_label')}</label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder={t('crypto.sync.target_placeholder')}
                autoFocus
              />
            </div>
            {hasTarget && (
              <p className={`crypto-sync-preview ${delta === 0 ? '' : delta > 0 ? 'up' : 'down'}`}>
                {delta === 0
                  ? t('crypto.sync.in_sync')
                  : delta > 0
                    ? t('crypto.sync.preview_up', { amount: fmt(delta) })
                    : t('crypto.sync.preview_down', { amount: fmt(Math.abs(delta)) })}
              </p>
            )}
          </>
        ) : (
          <>
            <div className="crypto-gl-toggle">
              <button
                type="button"
                className={`crypto-gl-btn ${glKind === 'gain' ? 'active gain' : ''}`}
                onClick={() => setGlKind('gain')}
              >
                {t('crypto.kind.gain')}
              </button>
              <button
                type="button"
                className={`crypto-gl-btn ${glKind === 'loss' ? 'active loss' : ''}`}
                onClick={() => setGlKind('loss')}
              >
                {t('crypto.kind.loss')}
              </button>
            </div>
            <div className="form-group">
              <label>{t('crypto.adjust.amount')}</label>
              <Input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0.01"
                value={glAmount}
                onChange={(e) => setGlAmount(e.target.value)}
                autoFocus
              />
            </div>
          </>
        )}

        <div className="form-group">
          <label>{t('crypto.adjust.note')}</label>
          <Input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('crypto.adjust.note_placeholder')}
            maxLength={120}
          />
        </div>

        <p className="crypto-local-note">{t('crypto.sync_note')}</p>

        <div className="crypto-modal-actions">
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={mode === 'sync' ? !hasTarget || delta === 0 : !glValid}
          >
            {t('crypto.adjust.save')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ============================================================
// Adjustment history row (two-tap delete, no popup)
// ============================================================

function AdjustmentRow({ adjustment }: { adjustment: HoldingsAdjustment }) {
  const { t } = useLanguage();
  const [confirming, setConfirming] = useState(false);
  const positive = adjustment.amount > 0;

  return (
    <div className="crypto-history-row">
      <div className="crypto-history-info">
        <span className="crypto-history-kind">{t(`crypto.kind.${adjustment.kind}`)}</span>
        <span className="crypto-history-meta">
          {fmtDate(adjustment.date)}
          {adjustment.note ? ` · ${adjustment.note}` : ''}
        </span>
      </div>
      <span className={`crypto-history-amount ${positive ? 'up' : 'down'}`}>
        {positive ? '+' : '-'}{fmt(Math.abs(adjustment.amount))}
      </span>
      <button
        className={`crypto-history-delete ${confirming ? 'confirming' : ''}`}
        onClick={() => {
          if (confirming) void removeAdjustment(adjustment.id);
          else setConfirming(true);
        }}
        onBlur={() => setConfirming(false)}
        aria-label={t('crypto.history.delete_aria')}
      >
        {confirming ? t('crypto.history.confirm_delete') : (
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
      </button>
    </div>
  );
}

// ============================================================
// One tracked group (hero + breakdown + actions + lists)
// ============================================================

function HoldingCard({ snapshot, onRefresh }: { snapshot: HoldingsSnapshot; onRefresh: () => void }) {
  const { t } = useLanguage();
  const { isPrivacyMode } = usePrivacyMode();
  const [adjustMode, setAdjustMode] = useState<AdjustMode | null>(null);
  const [showAllAdjustments, setShowAllAdjustments] = useState(false);

  const masked = <span className="private-amount masked">••••</span>;
  const money = (v: number, signed = false) =>
    isPrivacyMode ? masked : <>{signed && v > 0 ? '+' : ''}{v < 0 ? '-' : ''}{fmt(Math.abs(v))}</>;

  const historyLimit = 5;
  const visibleAdjustments = showAllAdjustments
    ? snapshot.adjustments
    : snapshot.adjustments.slice(0, historyLimit);

  const searchQuery = snapshot.group.terms.slice(0, 6).join(', ');

  return (
    <div className="crypto-holding">
      {/* Hero */}
      <div className="crypto-hero">
        <div className="crypto-hero-head">
          <span className="crypto-hero-icon">{snapshot.group.icon || '🪙'}</span>
          <div className="crypto-hero-names">
            <span className="crypto-hero-name">{snapshot.group.name}</span>
            <span className="crypto-hero-sub">{t('crypto.auto_note', { count: snapshot.matchCount })}</span>
          </div>
        </div>
        <div className="crypto-hero-value">
          {isPrivacyMode ? masked : fmt(snapshot.trackedValue)}
        </div>
        <span className="crypto-hero-label">{t('crypto.tracked_value')}</span>
      </div>

      {/* Actions */}
      <div className="crypto-actions">
        <Button variant="primary" onClick={() => setAdjustMode('sync')}>
          {t('crypto.actions.set_value')}
        </Button>
        <Button variant="secondary" onClick={() => setAdjustMode('gainloss')}>
          {t('crypto.actions.add_gain_loss')}
        </Button>
      </div>

      {/* Breakdown */}
      <div className="crypto-card">
        <div className="crypto-card-title">{t('crypto.breakdown.title')}</div>
        <div className="crypto-breakdown">
          <div className="crypto-breakdown-row">
            <span>{t('crypto.breakdown.moved_out')}</span>
            <strong>{money(snapshot.movedOut)}</strong>
          </div>
          <div className="crypto-breakdown-row">
            <span>{t('crypto.breakdown.moved_back')}</span>
            <strong>{money(-snapshot.movedBack)}</strong>
          </div>
          <div className="crypto-breakdown-row">
            <span>{t('crypto.breakdown.adjustments')}</span>
            <strong>{money(snapshot.adjustmentTotal, true)}</strong>
          </div>
          <div className="crypto-breakdown-row total">
            <span>{t('crypto.breakdown.tracked')}</span>
            <strong>{isPrivacyMode ? masked : fmt(snapshot.trackedValue)}</strong>
          </div>
        </div>
      </div>

      {/* Adjustment history */}
      {snapshot.adjustments.length > 0 && (
        <div className="crypto-card">
          <div className="crypto-card-title">{t('crypto.history.title')}</div>
          {visibleAdjustments.map((a) => (
            <AdjustmentRow key={a.id} adjustment={a} />
          ))}
          {snapshot.adjustments.length > historyLimit && (
            <button
              className="crypto-show-more"
              onClick={() => setShowAllAdjustments((v) => !v)}
            >
              {showAllAdjustments
                ? t('crypto.history.show_less')
                : t('crypto.history.show_all', { count: snapshot.adjustments.length })}
            </button>
          )}
        </div>
      )}

      {/* Recent detected transactions */}
      {snapshot.recentMatches.length > 0 && (
        <div className="crypto-card">
          <div className="crypto-card-title">{t('crypto.recent.title')}</div>
          {snapshot.recentMatches.map((m) => (
            <div key={m.id} className="crypto-recent-row">
              <div className="crypto-recent-info">
                <span className="crypto-recent-desc">{m.description}</span>
                <span className="crypto-recent-date">{fmtDate(m.date)}</span>
              </div>
              <span className={`crypto-recent-amount ${m.type === 'expense' ? 'out' : 'back'}`}>
                {m.type === 'expense' ? t('crypto.recent.out') : t('crypto.recent.back')}
                {' '}{isPrivacyMode && m.type === 'income' ? masked : fmt(m.amount)}
              </span>
            </div>
          ))}
          <Link className="crypto-view-all" to={`/transactions?q=${encodeURIComponent(searchQuery)}`}>
            {t('crypto.recent.view_all')}
          </Link>
        </div>
      )}

      {/* Detection hint */}
      <p className="crypto-detect-hint">
        {t('crypto.detect_hint', { name: snapshot.group.name })}{' '}
        <Link to="/analytics">{t('crypto.detect_hint_link')}</Link>
      </p>

      <AdjustModal
        snapshot={adjustMode ? snapshot : null}
        mode={adjustMode || 'sync'}
        onClose={() => setAdjustMode(null)}
        onSaved={onRefresh}
      />
    </div>
  );
}

// ============================================================
// Page
// ============================================================

export default function CryptoPage() {
  const { t } = useLanguage();
  const { holdings, loading, refresh } = useHoldings();

  return (
    <div className="crypto-page animate-fade">
      <div className="page-header">
        <h1 className="page-title">{t('crypto.title')}</h1>
        <PrivacyToggle />
      </div>

      {loading ? (
        <div className="crypto-loading">{t('common.loading')}</div>
      ) : holdings.length === 0 ? (
        <div className="empty">
          <div className="empty-icon-circle"><span>🪙</span></div>
          <div className="empty-title">{t('crypto.empty.title')}</div>
          <div className="empty-text">{t('crypto.empty.text')}</div>
          <Link to="/analytics" className="btn btn-primary mt-4">
            {t('crypto.empty.go_analytics')}
          </Link>
        </div>
      ) : (
        holdings.map((snapshot) => (
          <HoldingCard key={snapshot.group.id} snapshot={snapshot} onRefresh={refresh} />
        ))
      )}
    </div>
  );
}
